/**
 * Re-read the signed-in user from the server and merge it into the auth store.
 *
 * The store's user is otherwise seeded ONLY by `/auth/oidc/callback` and
 * `/auth/refresh`. Both used to return just `{ id, email, name }`, so every
 * surface that renders `user.avatarUrl` (dashboard header, presence Settings)
 * showed initials while My Profile — the one screen that calls `GET /users/me`
 * itself — showed the real picture.
 *
 * The backend now sends `avatarUrl` in those payloads, which makes this
 * redundant for NEW sessions. It stays for two reasons that outlive that fix:
 *
 *   1. Sessions minted before the fix hold a user blob with no picture, and
 *      they are valid for up to 30 days. Without this they show initials until
 *      the user signs out and back in.
 *   2. A picture uploaded on another device (or a role change) reaches this tab
 *      at the next startup rather than at the next full re-login.
 *
 * Failures are silent by design: this is a background top-up of something the
 * app already has a value for, so a network blip must not surface an error or
 * disturb the session.
 */

import { config } from '@/config/env'
import { useAuthStore } from '../store/auth.store'

interface MeResponse {
  user: {
    id: string
    email: string
    name: string | null
    avatarUrl: string | null
    role: string | null
  }
}

export async function syncUserFromServer(): Promise<void> {
  const { user, session } = useAuthStore.getState()
  if (!user || !session?.accessToken) return
  if (session.expiresAt <= Date.now()) return

  try {
    const res = await fetch(`${config.apiUrl}/api/v1/users/me`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${session.accessToken}` },
    })
    if (!res.ok) return

    const { user: me } = (await res.json()) as MeResponse
    // A response for a different account means the session changed mid-flight
    // (signed out and back in as someone else). Merging it would paint the new
    // user's header with the old user's details.
    if (!me?.id || me.id !== useAuthStore.getState().user?.id) return

    useAuthStore.getState().mergeUser({
      // `avatarUrl` is set unconditionally: the server is authoritative even
      // when it says null, so removing a picture has to CLEAR the stored one
      // rather than fall through to it. `name`/`role` are only overwritten
      // when the server actually has a value — a null there is missing data,
      // not an instruction to blank the header.
      avatarUrl: me.avatarUrl ?? undefined,
      ...(me.name ? { name: me.name } : {}),
      ...(me.role ? { role: me.role } : {}),
    })
  } catch {
    /* offline or DNS failure — the stored user stays as it was */
  }
}
